import { DEFAULT_VOICE, isVoice } from "../config/voices.js";

const VOICE_KEY = "paws.voice";

export function plainText(markdown = "") {
  return markdown
    .replace(/[#*_`>]/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function readVoice(storage = globalThis.localStorage) {
  try {
    const stored = storage?.getItem(VOICE_KEY);
    return isVoice(stored) ? stored : DEFAULT_VOICE;
  } catch {
    return DEFAULT_VOICE;
  }
}

export function writeVoice(voice, storage = globalThis.localStorage) {
  if (!isVoice(voice)) return false;
  try {
    storage?.setItem(VOICE_KEY, voice);
    return true;
  } catch {
    return false;
  }
}

export function createSpeaker({
  client = null,
  synthesis = globalThis.speechSynthesis,
  storage = globalThis.localStorage,
  createAudio = (url) => new globalThis.Audio(url),
  onError = () => {}
} = {}) {
  const listeners = new Set();
  let speaking = false;
  let current = null;
  let token = 0;

  const announce = (value) => {
    if (speaking === value) return;
    speaking = value;
    for (const listener of listeners) listener(speaking);
  };

  const release = () => {
    if (!current) return;
    current.onended = null;
    current.onerror = null;
    current.pause?.();
    current = null;
  };

  const stop = () => {
    token += 1;
    release();
    synthesis?.cancel?.();
    announce(false);
  };

  const fallback = (text) => {
    if (!synthesis || typeof globalThis.SpeechSynthesisUtterance !== "function") return false;
    synthesis.cancel();
    const utterance = new globalThis.SpeechSynthesisUtterance(text);
    utterance.lang = "it-IT";
    utterance.onend = () => announce(false);
    utterance.onerror = () => announce(false);
    synthesis.speak(utterance);
    announce(true);
    return true;
  };

  const online = () => globalThis.navigator?.onLine !== false;

  const stream = async (text, voice, mine) => {
    const url = await client.clip(text, voice);
    if (mine !== token) return false;
    const audio = createAudio(url);
    current = audio;
    audio.onended = () => {
      if (mine === token) {
        release();
        announce(false);
      }
    };
    audio.onerror = () => {
      if (mine !== token) return;
      release();
      announce(false);
      fallback(text);
    };
    await audio.play();
    return mine === token;
  };

  return {
    get available() {
      return Boolean(client?.available || synthesis);
    },
    get remote() {
      return Boolean(client?.available);
    },
    get speaking() {
      return speaking;
    },
    get voice() {
      return readVoice(storage);
    },
    setVoice(voice) {
      return writeVoice(voice, storage);
    },
    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    stop,
    async speak(text, { voice } = {}) {
      const clean = plainText(text);
      if (!clean) return false;
      stop();
      const mine = token;
      if (client?.available && online()) {
        announce(true);
        try {
          return await stream(clean, voice || readVoice(storage), mine);
        } catch (error) {
          if (mine !== token) return false;
          announce(false);
          onError(error);
        }
      }
      if (mine !== token) return false;
      return fallback(clean);
    }
  };
}
